import {
  deriveAktenAuthSecret,
  deriveAktenEncryptionKey,
  deriveMasterKey,
  derivePortalAuthSecret,
} from './kdf.ts';

/**
 * Das Passwort verlässt diesen Browser nicht.
 *
 * Überall dort, wo das Portal früher ein Passwort an den Server geschickt hat,
 * schickt es jetzt einen daraus abgeleiteten Beweis. Der Server speichert
 * davon nur einen scrypt-Hash und kann daraus weder das Passwort noch die
 * anderen Zweige des Hauptschlüssels gewinnen — einer davon schließt den
 * Akten-Safe auf.
 *
 * Es gibt genau diese eine Stelle, an der ein Beweis entsteht. Wer eine zweite
 * baut, riskiert eine Abweichung, und eine Abweichung sperrt Kunden aus.
 */

/**
 * Mindestlänge des Passworts.
 *
 * Diese Prüfung lag bis zur Umstellung beim Server. Sie musste hierher
 * wandern, weil dort nur noch der Beweis ankommt und man dem seine Länge
 * nicht ansieht. Wer sie im Browser umgeht, schwächt allein sein eigenes
 * Konto — das ist der Preis dafür, dass das Passwort hier bleibt.
 */
export const MIN_PASSWORTLAENGE = 10;

export const PASSWORTHINWEIS =
  'Bitte mindestens zehn Zeichen. Ein ganzer Satz ist leichter zu merken als ' +
  'ein kurzes Kryptisches.';

/**
 * Rechnet den Anmeldebeweis aus, ohne etwas zu behalten.
 *
 * Für Einladung und Passwortwechsel: Dort wird nur der Beweis gebraucht, also
 * hat der Hauptschlüssel danach auch nicht im Speicher zu liegen.
 *
 * Dauert auf älterer Hardware spürbar, weil Argon2id mit 64 MiB arbeitet —
 * die Aufrufer sollten so lange einen Wartezustand zeigen.
 */
export async function portalBeweis(email: string, passwort: string): Promise<string> {
  const hauptschluessel = await deriveMasterKey(email.trim().toLowerCase(), passwort);
  return derivePortalAuthSecret(hauptschluessel);
}

// ---------------------------------------------------------------------------
// Hauptschlüssel für den Akten-Safe
// ---------------------------------------------------------------------------
//
// Nur eine Modulvariable, wie im Akten-Safe selbst: kein localStorage, kein
// sessionStorage, kein IndexedDB. Ein Neuladen der Seite wirft ihn weg, und
// das ist Absicht — was im Speicher steht, verschwindet mit dem Tab.
//
// JavaScript kann Speicher nicht zuverlässig überschreiben; `vergessen()`
// entfernt die Referenz, mehr ist nicht möglich. Deshalb liegt hier auch nur
// der Hauptschlüssel und nicht zusätzlich das Passwort.

let hauptschluessel: Uint8Array | null = null;

/**
 * Wie `portalBeweis`, behält aber den Hauptschlüssel für die Übergabe an den
 * Akten-Safe. Ausschließlich für die Anmeldung.
 */
export async function anmeldungVorbereiten(email: string, passwort: string): Promise<string> {
  hauptschluessel = await deriveMasterKey(email.trim().toLowerCase(), passwort);
  return derivePortalAuthSecret(hauptschluessel);
}

export function aktenschrankBereit(): boolean {
  return hauptschluessel !== null;
}

/** Beim Abmelden und bei Sitzungsverlust. */
export function vergessen(): void {
  hauptschluessel = null;
}

export interface AktenUebergabe {
  email: string;
  anmeldebeweis: string;
  schluessel: Uint8Array;
}

/**
 * Die beiden Werte, die der Akten-Safe braucht: ein Anmeldebeweis für seine
 * API und der Schlüssel, der dort den privaten Schlüssel auspackt.
 *
 * Gibt `null` zurück, wenn der Hauptschlüssel nicht (mehr) vorliegt — nach
 * einem Neuladen etwa. Dann bleibt dem Kunden der gewohnte Weg über die
 * Passwortmaske des Akten-Safes.
 */
export async function aktenUebergabe(email: string): Promise<AktenUebergabe | null> {
  if (!hauptschluessel) return null;
  const adresse = email.trim().toLowerCase();
  return {
    email: adresse,
    anmeldebeweis: await deriveAktenAuthSecret(hauptschluessel),
    schluessel: await deriveAktenEncryptionKey(hauptschluessel),
  };
}

/** Prüft die Länge und liefert den Hinweis, falls sie nicht reicht. */
export function passwortBeanstandung(passwort: string): string | null {
  return passwort.length < MIN_PASSWORTLAENGE ? PASSWORTHINWEIS : null;
}
